// components/MyPage/MyReports.jsx
import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Alert,
  CircularProgress, Stack, Pagination, useMediaQuery
} from '@mui/material';
import ReportIcon from '@mui/icons-material/Report';
import SafeChip from '../Common/SafeChip';
import api from '../../api/axios';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material/styles';

/* ── 설정 ─────────────────────────── */
const PAGE_SIZE = 7;

/* ── 매핑 ─────────────────────────── */
const STATUS_LABEL = {
  pending: '대기 중',
  blinded: '블라인드',
  deleted: '삭제',
  ignored: '무시됨',
  restored: '복구됨',
};
const TYPE_LABEL = {
  post: '게시글',
  comment: '댓글',
  reply: '대댓글',
  user: '회원',
};
const statusColor = s => {
  switch (s) {
    case 'blinded': return 'warning';
    case 'deleted': return 'error';
    case 'restored': return 'primary';
    default: return 'default';
  }
};

/* ── 컴포넌트 ──────────────────────── */
export default function MyReports() {
  const [logs, setLogs] = useState(null);  // 전체 데이터
  const [err, setErr] = useState('');
  const [page, setPage] = useState(1);     // 현재 페이지

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /* 데이터 로딩 */
  useEffect(() => {
    api.get('/api/reports/my-reports/')
      .then(r => {
        const sorted = (r.data.reports || [])
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setLogs(sorted);
      })
      .catch(() => { setErr('신고 내역을 불러오지 못했습니다.'); setLogs([]); });
  }, []);

  if (err) return <Alert severity="error">{err}</Alert>;
  if (logs === null) return <CircularProgress />;
  if (!logs.length) return <Typography align="center" sx={{ mt: 2 }}>📭 신고한 내역이 없습니다.</Typography>;

  /* ── 페이지 계산 ─────────────────── */
  const pageCount = Math.ceil(logs.length / PAGE_SIZE);
  const slice = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto' }}>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        📢 내가 신고한 내역
      </Typography>

      {/* 카드 리스트 */}
      {slice.map(rep => (
        <Card
          key={rep.id}
          variant="outlined"
          sx={{
            mb: 2, px: 2, py: 1.5,
            '&:hover': { boxShadow: 3, transition: '.15s' }
          }}
        >
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {/* 헤더 */}
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={1}
              alignItems={isMobile ? 'flex-start' : 'center'}
              mb={.5}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReportIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight={600}>
                  [{TYPE_LABEL[rep.report_type] || rep.report_type}] {rep.target || '(대상 없음)'}
                </Typography>
              </Box>
              <Box flexGrow={1} />
              <SafeChip
                label={STATUS_LABEL[rep.status] || rep.status}
                color={statusColor(rep.status)}
                size="small"
              />
            </Stack>

            {/* 사유 & 날짜 */}
            <Typography variant="body2" color="text.secondary" gutterBottom>
              사유: {rep.reason || '미입력'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              신고일: {dayjs(rep.created_at).format('YYYY. M. D. HH:mm')}
            </Typography>
          </CardContent>
        </Card>
      ))}

      {/* 페이지네이션 (7건 이상일 때만) */}
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            showFirstButton
            showLastButton
            siblingCount={1}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}